import React, { useState, useEffect, useRef } from "react";
import { Modal, Form, Input, Button, Typography, Alert, Flex, Row, Col, message, Tabs } from "antd";
import { MailOutlined, LockOutlined, MobileOutlined, TeamOutlined, CopyOutlined, CheckOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { setSession, fetchUserProfile } from "../../../redux/slices/sessionSlice";
import { setCompanyInfo } from "../../../redux/slices/executorSlice";
import { loginUser, loginEmployee, verifyEmployee2FASetup, verifyEmployee2FACode, verifyAdmin2FACode } from "../../../services/authService";
import logoImg from "../../assets/logo/dealVoice.png";
import heroImg from "../../assets/landing-page/hero page.png";
import "./auth2fa.css";

const { Title, Text, Paragraph } = Typography;

/**
 * Minimal & Professional 6-Slot OTP Component
 */
const OtpInput = ({ value, onChange, onComplete, autoFocus }) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
    onChange(val);
    if (val.length === 6 && onComplete) {
      onComplete(val);
    }
  };

  return (
    <label className="auth-2fa-otp-wrapper" htmlFor="auth-2fa-otp-input">
      <input id="auth-2fa-otp-input" ref={inputRef} type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} value={value} onChange={handleChange} className="auth-2fa-otp-hidden-input" autoFocus={autoFocus} />
      <div className="auth-2fa-otp-grid">
        {[0, 1, 2, 3, 4, 5].map((idx) => {
          const char = value[idx] || "";
          const isFilled = value.length > idx;
          const isActive = value.length === idx;
          let slotContent = "";
          if (char) {
            slotContent = char;
          } else if (isActive) {
            slotContent = <span className="auth-2fa-cursor">|</span>;
          }
          return (
            <div key={idx} className={`auth-2fa-otp-slot ${isFilled ? "filled" : ""} ${isActive ? "active" : ""}`}>
              {slotContent}
            </div>
          );
        })}
      </div>
    </label>
  );
};

function LoginModal({ isOpen, closeModals, openSignup, openForgotPwd, onLoginSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [authStep, setAuthStep] = useState("credentials"); // 'credentials' | 'setup_2fa' | 'verify_2fa'
  const [twoFactorData, setTwoFactorData] = useState({
    qrCode: "",
    secret: "",
    otpauthUrl: "",
    employeeId: "",
    userId: "",
    employeeName: "",
    companyCode: "",
    role: "employee",
  });
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);

  const [companyForm] = Form.useForm();
  const [employeeForm] = Form.useForm();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isOpen) {
      setError("");
      setAuthStep("credentials");
      setTwoFactorCode("");
      setTwoFactorLoading(false);
      companyForm.resetFields();
      employeeForm.resetFields();
    }
  }, [isOpen, companyForm, employeeForm]);

  const completeCompanyLoginSuccess = (data, companyCode) => {
    const userObj = data.user || {};
    const effectiveCompanyCode = userObj.companyCode || companyCode;

    // Dispatch to Redux store
    dispatch(
      setSession({
        token: data.token,
        userInfo: {
          ...userObj,
          id: userObj.id || userObj._id,
          companyCode: effectiveCompanyCode,
          companyName: userObj.companyName,
          role: userObj.role || "company_admin",
        },
      }),
    );

    dispatch(
      setCompanyInfo({
        companyCode: effectiveCompanyCode,
        companyName: userObj.companyName,
        companyAddress: userObj.companyAddress,
        callStatus: userObj.leadStatuses || userObj.callStatus,
      }),
    );

    dispatch(fetchUserProfile(effectiveCompanyCode));

    message.success(data.message || "Sign in successful!");
    setLoading(false);
    closeModals();

    if (onLoginSuccess) {
      onLoginSuccess(userObj);
    }
  };

  const onCompanyFinish = async (values) => {
    setError("");
    setLoading(true);
    try {
      const data = await loginUser({
        email: values.email,
        password: values.password,
      });

      if (data.require2FA) {
        setAuthStep("verify_2fa");
        setTwoFactorData({
          qrCode: "",
          secret: "",
          otpauthUrl: "",
          userId: data.userId || "",
          employeeId: data.userId || "",
          employeeName: data.name || data.email || "Admin",
          companyCode: data.companyCode || "",
          role: "company_admin",
        });
        setTwoFactorCode("");
        setError("");
        setLoading(false);
        return;
      }

      completeCompanyLoginSuccess(data, values.companyCode);
    } catch (err) {
      setLoading(false);
      setError(err.message || "Login failed. Please verify your credentials.");
    }
  };

  const completeEmployeeLoginSuccess = (data, companyCode) => {
    const empObj = data.employee || data.user || {};
    const effectiveCompanyCode = companyCode?.trim() || empObj.companyCode;

    dispatch(
      setSession({
        token: data.token,
        userInfo: {
          ...empObj,
          id: empObj.id || empObj._id,
          companyCode: effectiveCompanyCode,
          companyName: empObj.companyName,
          role: "employee",
        },
      }),
    );

    dispatch(
      setCompanyInfo({
        companyCode: effectiveCompanyCode,
        companyName: empObj.companyName,
      }),
    );

    dispatch(fetchUserProfile(effectiveCompanyCode));

    message.success(data.message || "Employee sign in successful!");
    closeModals();

    if (onLoginSuccess) {
      onLoginSuccess({
        ...empObj,
        companyCode: effectiveCompanyCode,
        role: "employee",
      });
    }
  };

  const onEmployeeFinish = async (values) => {
    setError("");
    setLoading(true);
    try {
      const data = await loginEmployee({
        companyCode: values.companyCode,
        mobile: values.mobile,
        countryCode: "+91",
        isWeb: true,
      });

      setLoading(false);

      if (data.require2FASetup) {
        setAuthStep("setup_2fa");
        setTwoFactorData({
          qrCode: data.qrCode || "",
          secret: data.secret || "",
          otpauthUrl: data.otpauthUrl || "",
          employeeId: data.employeeId || "",
          employeeName: data.employeeName || "",
          companyCode: data.companyCode || values.companyCode?.trim(),
        });
        setTwoFactorCode("");
        setError("");
        return;
      }

      if (data.require2FA) {
        setAuthStep("verify_2fa");
        setTwoFactorData({
          qrCode: "",
          secret: "",
          otpauthUrl: "",
          employeeId: data.employeeId || "",
          employeeName: data.employeeName || "",
          companyCode: data.companyCode || values.companyCode?.trim(),
        });
        setTwoFactorCode("");
        setError("");
        return;
      }

      completeEmployeeLoginSuccess(data, values.companyCode);
    } catch (err) {
      setLoading(false);
      setError(err.message || "Employee login failed. Please verify your credentials.");
    }
  };

  const handleVerifySetup = async (codeToVerify) => {
    const code = (codeToVerify || twoFactorCode).trim();
    if (code?.length !== 6) {
      setError("Please enter the complete 6-digit code from your authenticator app.");
      return;
    }
    setError("");
    setTwoFactorLoading(true);
    try {
      const res = await verifyEmployee2FASetup({
        employeeId: twoFactorData.employeeId,
        companyCode: twoFactorData.companyCode,
        token: code,
      });
      setTwoFactorLoading(false);
      completeEmployeeLoginSuccess(res, twoFactorData.companyCode);
    } catch (err) {
      setTwoFactorLoading(false);
      setError(err.message || "Invalid 6-digit verification code. Please check your Authenticator app and try again.");
    }
  };

  const handleVerifyCode = async (codeToVerify) => {
    const code = (codeToVerify || twoFactorCode).trim();
    if (code?.length !== 6) {
      setError("Please enter the 6-digit code from your authenticator app.");
      return;
    }
    setError("");
    setTwoFactorLoading(true);
    try {
      if (twoFactorData.role === "company_admin") {
        const res = await verifyAdmin2FACode({
          userId: twoFactorData.userId || twoFactorData.employeeId,
          companyCode: twoFactorData.companyCode,
          token: code,
        });
        setTwoFactorLoading(false);
        completeCompanyLoginSuccess(res, twoFactorData.companyCode);
      } else {
        const res = await verifyEmployee2FACode({
          employeeId: twoFactorData.employeeId,
          companyCode: twoFactorData.companyCode,
          token: code,
        });
        setTwoFactorLoading(false);
        completeEmployeeLoginSuccess(res, twoFactorData.companyCode);
      }
    } catch (err) {
      setTwoFactorLoading(false);
      setError(err.message || "Invalid code. Please check your Authenticator app and try again.");
    }
  };

  const copySecretKey = () => {
    if (!twoFactorData.secret) return;
    navigator.clipboard?.writeText(twoFactorData.secret);
    setCopiedSecret(true);
    message.success("Secret key copied to clipboard!");
    setTimeout(() => setCopiedSecret(false), 2000);
  };

  const backToCredentials = () => {
    setAuthStep("credentials");
    setTwoFactorCode("");
    setError("");
    setTwoFactorLoading(false);
  };

  return (
    <Modal
      title={null}
      footer={null}
      onCancel={closeModals}
      open={isOpen}
      width={1250}
      centered
      closable={true}
      styles={{
        body: {
          padding: 0,
          overflow: "hidden",
          borderRadius: 16,
          height: "75vh",
          maxHeight: "75vh",
        },
      }}
    >
      <Row style={{ height: "75vh", maxHeight: "75vh", margin: 0 }}>
        {/* Left Side - Hero Content */}
        <Col xs={0} md={12} lg={14} style={{ background: "#f8f9fa", padding: "2.5rem 3.5rem", display: "flex", flexDirection: "column", justifyContent: "center", borderRadius: 16, height: "100%", overflow: "hidden" }}>
          <Title level={1} style={{ fontSize: "2.5rem", lineHeight: 1.15, marginBottom: "0.75rem", color: "#0f172a", letterSpacing: "-0.03em" }}>
            <span style={{ color: "#8d8d8d", fontWeight: 500 }}>The Intelligent CRM for</span>
            <br />
            sales deal closing.
          </Title>
          <Paragraph style={{ fontSize: "0.95rem", color: "#64748b", marginBottom: "1.25rem", maxWidth: 520 }}>DealVoice automates the heavy lifting of sales pipeline management. Live in hours. Learning from every deal.</Paragraph>
          <img src={heroImg} alt="Dashboard Preview" style={{ width: "100%", maxWidth: "100%", borderRadius: 14, boxShadow: "0 15px 35px rgba(0,0,0,0.08)", objectFit: "contain" }} />
        </Col>

        {/* Right Side - Form Container */}
        <Col xs={24} md={12} lg={10} className="hide-scrollbar" style={{ padding: "1.75rem 2.5rem", display: "flex", flexDirection: "column", justifyContent: "center", background: "#fff", height: "100%", overflowY: "auto", borderTopRightRadius: 16, borderBottomRightRadius: 16 }}>
          {authStep === "credentials" && (
            <div style={{ maxWidth: 360, width: "100%", margin: "0 auto" }}>
              <div style={{ marginBottom: "1.5rem" }}>
                <img src={logoImg} alt="DealVoice Logo" style={{ height: 32, objectFit: "contain" }} />
              </div>
              <Title level={2} style={{ margin: "0 0 0.5rem" }}>
                Welcome back
              </Title>
              <Paragraph type="secondary" style={{ marginBottom: "2rem" }}>
                Sign in to your account
              </Paragraph>

              {error && <Alert message={error} type="error" showIcon style={{ marginBottom: "1.5rem" }} />}

              <Tabs
                defaultActiveKey="1"
                animated={{ inkBar: true, tabPane: true }}
                style={{ minHeight: 320 }}
                items={[
                  {
                    key: "1",
                    label: "Admin / Company",
                    children: (
                      <Form form={companyForm} layout="vertical" onFinish={onCompanyFinish} requiredMark={false}>
                        <Form.Item
                          label="Email"
                          name="email"
                          rules={[
                            { required: true, message: "Please enter your email" },
                            { type: "email", message: "Please enter a valid email" },
                          ]}
                        >
                          <Input size="large" prefix={<MailOutlined style={{ color: "#94a3b8" }} />} placeholder="you@company.com" />
                        </Form.Item>

                        <Form.Item label="Password" name="password" rules={[{ required: true, message: "Please enter your password" }]}>
                          <Input.Password size="large" prefix={<LockOutlined style={{ color: "#94a3b8" }} />} placeholder="••••••••" />
                        </Form.Item>

                        <Flex justify="end" style={{ marginBottom: "1.5rem" }}>
                          <Button
                            type="link"
                            style={{ padding: 0, fontSize: "0.85rem" }}
                            onClick={(e) => {
                              e.preventDefault();
                              openForgotPwd();
                            }}
                          >
                            Forgot password?
                          </Button>
                        </Flex>

                        <Form.Item>
                          <Button type="primary" htmlType="submit" size="large" block loading={loading} style={{ height: 44, borderRadius: 12, fontWeight: 600 }}>
                            Log In
                          </Button>
                        </Form.Item>
                      </Form>
                    ),
                  },
                  {
                    key: "2",
                    label: "Employee",
                    children: (
                      <Form form={employeeForm} layout="vertical" onFinish={onEmployeeFinish} requiredMark={false}>
                        <Form.Item label="Company Code" name="companyCode" rules={[{ required: true, message: "Please enter company code" }]}>
                          <Input size="large" prefix={<TeamOutlined style={{ color: "#94a3b8" }} />} placeholder="Enter company code" />
                        </Form.Item>

                        <Form.Item label="Mobile Number" name="mobile" rules={[{ required: true, message: "Please enter your mobile number" }]}>
                          <Input size="large" prefix={<MobileOutlined style={{ color: "#94a3b8" }} />} placeholder="10-digit mobile number" />
                        </Form.Item>

                        <Form.Item style={{ marginTop: "1rem" }}>
                          <Button type="primary" htmlType="submit" size="large" block loading={loading} style={{ height: 44, borderRadius: 12, fontWeight: 600 }}>
                            Log In
                          </Button>
                        </Form.Item>
                      </Form>
                    ),
                  },
                ]}
              />

              <div style={{ textAlign: "center", marginTop: "1rem" }}>
                <Text type="secondary">
                  Don't have an account?{" "}
                  <Button
                    type="link"
                    style={{ padding: 0, fontWeight: 600 }}
                    onClick={(e) => {
                      e.preventDefault();
                      openSignup(false);
                    }}
                  >
                    Sign Up
                  </Button>
                </Text>
              </div>
            </div>
          )}

          {authStep === "setup_2fa" && (
            <div className="auth-2fa-container">
              <div className="auth-2fa-kicker">Security Setup</div>
              <h2 className="auth-2fa-title">Setup Authenticator</h2>
              <p className="auth-2fa-description">
                Scan this QR code using the Authenticator app <strong>AuthSoft</strong> to activate daily 2FA login.
              </p>

              <a href="https://play.google.com/store/apps/details?id=com.softrate.authsoft&pcampaignid=web_share" target="_blank" rel="noopener noreferrer" className="authsoft-download-pill">
                <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor">
                  <path d="M3.609 1.814L13.793 12 3.61 22.186A1.5 1.5 0 0 1 3 21.2V2.8a1.5 1.5 0 0 1 .609-.986zm11.3 11.3l2.296 2.295-9.845 5.684 7.549-7.979zm2.296-3.71l-2.296 2.296-7.55-7.98 9.846 5.684zm1.118.646l2.84 1.64a.8.8 0 0 1 0 1.388l-2.84 1.64-2.148-2.334 2.148-2.334z" />
                </svg>
                <span>Get AuthSoft on Google Play</span>
                <span style={{ fontSize: 10, opacity: 0.7 }}>↗</span>
              </a>

              <div className="auth-2fa-qr-card">
                <div className="auth-2fa-account-badge">
                  <span>DealVoice</span>
                  <span>•</span>
                  <span className="highlight">{twoFactorData.employeeName || "Employee"}</span>
                </div>

                {twoFactorData.qrCode && (
                  <div className="auth-2fa-qr-wrapper">
                    <img src={twoFactorData.qrCode} alt="DealVoice TOTP QR Code" className="auth-2fa-qr-image" />
                  </div>
                )}

                {twoFactorData.secret && (
                  <div className="auth-2fa-secret-box">
                    <div className="auth-2fa-secret-label">Can't scan? Copy key:</div>
                    <div className="auth-2fa-secret-row">
                      <span className="auth-2fa-secret-code">{twoFactorData.secret}</span>
                      <button type="button" className="auth-2fa-copy-btn" onClick={copySecretKey}>
                        {copiedSecret ? <CheckOutlined style={{ color: "#16a34a" }} /> : <CopyOutlined />}
                        <span>{copiedSecret ? "Copied" : "Copy"}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="auth-2fa-otp-section">
                <span className="auth-2fa-otp-label">Enter 6-digit Authenticator Code</span>
                <OtpInput
                  value={twoFactorCode}
                  onChange={(val) => {
                    setTwoFactorCode(val);
                    if (error) setError("");
                  }}
                  onComplete={handleVerifySetup}
                  autoFocus
                />
              </div>

              {error && <Alert message={error} type="error" showIcon style={{ marginBottom: "12px", fontSize: "0.82rem" }} />}

              <Button type="primary" size="large" block className="auth-2fa-submit-btn" loading={twoFactorLoading} disabled={twoFactorCode.length !== 6} onClick={() => handleVerifySetup(twoFactorCode)}>
                Verify & Activate
              </Button>

              <button type="button" className="auth-2fa-back-link" onClick={backToCredentials}>
                ← Back to Sign In
              </button>
            </div>
          )}

          {authStep === "verify_2fa" && (
            <div className="auth-2fa-container">
              <div className="auth-2fa-kicker">Two-Factor Authentication</div>
              <h2 className="auth-2fa-title">Enter Authenticator Code</h2>
              <p className="auth-2fa-description">
                Enter the 6-digit code from your Authenticator app for <strong>DealVoice ({twoFactorData.employeeName || "Employee"})</strong>.
              </p>

              <div className="auth-2fa-otp-section" style={{ marginTop: 16 }}>
                <span className="auth-2fa-otp-label">6-Digit Code</span>
                <OtpInput
                  value={twoFactorCode}
                  onChange={(val) => {
                    setTwoFactorCode(val);
                    if (error) setError("");
                  }}
                  onComplete={handleVerifyCode}
                  autoFocus
                />
              </div>

              {error && <Alert message={error} type="error" showIcon style={{ marginBottom: "12px", fontSize: "0.82rem" }} />}

              <Button type="primary" size="large" block className="auth-2fa-submit-btn" loading={twoFactorLoading} disabled={twoFactorCode.length !== 6} onClick={() => handleVerifyCode(twoFactorCode)}>
                Verify & Enter Workspace
              </Button>

              <button type="button" className="auth-2fa-back-link" onClick={backToCredentials}>
                ← Back to Sign In
              </button>
            </div>
          )}
        </Col>
      </Row>
    </Modal>
  );
}

export default LoginModal;
